/**
 * PSTUDIO-4784. True when a keyboard event's target is a `ps-select` that lets
 * the user type a value (`allow-custom-value`).
 *
 * Such a select keeps a real text <input> in its shadow root. A listener on the
 * document sees the event retargeted to the `ps-select` HOST, so the usual
 * "is the target an <input>?" test (`instanceof HTMLInputElement`, a tag name of
 * `INPUT`) says no, and the grid would treat a typed character as a grid
 * shortcut or as the start of editing the cell behind the select.
 *
 * The `allowCustomValue` PROPERTY is read, not the attribute: the React wrapper
 * sets the property and the component does not reflect it. The check is
 * duck-typed (no `instanceof`) so it holds for a target from another realm and
 * for a plain object in a test.
 */
export function isEditableSelectTarget(target: unknown): boolean {
  const el = target as { tagName?: string; allowCustomValue?: boolean } | null;
  return (
    el?.tagName?.toUpperCase() === 'PS-SELECT' && el.allowCustomValue === true
  );
}

/**
 * True when the target is such a select AND its list is open. Typing opens the
 * list, so "open" covers a half-typed value as well as a plain open list. Escape
 * belongs to the select then: it drops the typed text and closes the list. A
 * grid-level Escape (closing a side panel) must wait for the next press, or it
 * would take the keystroke before the select sees it and tear the select down
 * with its text still uncommitted.
 */
export function isOpenEditableSelect(target: unknown): boolean {
  return (
    isEditableSelectTarget(target) &&
    (target as { open?: boolean }).open === true
  );
}

/**
 * Release 29817 review. The td an Escape should hand focus back to, when the
 * key comes from such a select that sits in a grid cell and whose list is
 * CLOSED; null otherwise.
 *
 * In the grid, Escape in a cell editor returns focus to its td through
 * table-nav's `cellNavigation`. A `ps-select` never lets the key get that far:
 * its combobox keydown handler (`handleComboboxKeyDown`, design-system
 * select.wc.ts) stops every key but Tab inside its shadow root. A
 * document-level CAPTURE listener still sees it first, which is where this is
 * called from (table.tsx).
 *
 * An OPEN list is left alone: there Escape belongs to the select, which reverts
 * the typed text and closes the list. A select outside any td — the Expanded
 * Cell View's — gets null, so its Escape keeps closing the panel.
 */
export function cellToFocusOnClosedSelectEscape(event: {
  key: string;
  target: EventTarget | null;
}): HTMLElement | null {
  if (
    event.key !== 'Escape' ||
    !isEditableSelectTarget(event.target) ||
    isOpenEditableSelect(event.target)
  ) {
    return null;
  }
  return (event.target as Element).closest<HTMLElement>('td');
}
